import {
	type ConfluenceRangeFilter,
	type ThirdPartyFilters,
} from '@atlassian/dynamic-cards-search-client';

import { ThirdPartyConfigs } from '../../constants/3p-product-configs';
import { type FilterId } from '../../constants/filters';
import {
	products as productConfigs,
	type ProductKeys,
	type ProductKeys3P,
} from '../../constants/products';
import { ProductKey } from '../../constants/schemas/query-params';
import type { SelectedFiltersType } from '../../constants/types';
import { type OutboundAuthConfigsType } from '../../types';

import { getTypeMapping, type TypeFilterValueKey } from './constants';

const GOOGLE_INTEGRATION_ARI = 'ari:cloud:platform::integration/google';
const MICROSOFT_INTEGRATION_ARI = 'ari:cloud:platform::integration/microsoft';
const WEB_CRAWLER_INTEGRATION_ARI = 'ari:cloud:platform::integration/atlassian-web-crawler';
const GITHUB_INTEGRATION_ARI = 'ari:cloud:platform::integration/github';
const TEAMS_INTEGRATION_ARI = 'ari:cloud:platform::integration/microsoft';
const FIGMA_INTEGRATION_ARI = 'ari:cloud:platform::integration/figma';
const LOOM_INTEGRATION_ARI = 'ari:cloud:platform::integration/loom';
const DROPBOX_INTEGRATION_ARI = 'ari:cloud:platform::integration/dropbox';
const SLACK_INTEGRATION_ARI = 'ari:cloud:platform::integration/slack';
const AIRTABLE_INTEGRATION_ARI = 'ari:cloud:platform::integration/airtable';
const BITBUCKET_INTEGRATION_ARI = 'ari:cloud:platform::integration/bitbucket';
const NOTION_INTEGRATION_ARI = 'ari:cloud:platform::integration/notion';
const MIRO_INTEGRATION_ARI = 'ari:cloud:platform::integration/miro';

export const getEmptySelectedFilters = (): SelectedFiltersType => ({
	commonFilters: {},
	confluenceFilters: {
		spacesFilter: [],
		contributorsFilter: [],
		ancestorIdsFilter: [],
		range: [],
		owners: [],
		labelsFilter: [],
	},
	jiraFilters: {
		issueFilter: {
			assigneeARIs: [],
			projectARIs: [],
		},
	},
	thirdPartyFilters: {
		titleMatchOnly: false,
		subtypes: [],
		integrations: [],
	},
});

export const convertCustomDateToRange = (from?: string, to?: string) => {
	const fromDate = from ? new Date(from) : new Date();
	const startOfTheFromDate = new Date(
		fromDate.getFullYear(),
		fromDate.getMonth(),
		fromDate.getDate(),
		0,
		0,
		0,
		0,
	);
	const toDate = to ? new Date(to) : new Date();
	const endOfTheToDate = new Date(
		toDate.getFullYear(),
		toDate.getMonth(),
		toDate.getDate(),
		23,
		59,
		59,
		999,
	);
	return {
		gt: from ? startOfTheFromDate.toISOString().split('Z')[0] : undefined,
		lt: to ? endOfTheToDate.toISOString().split('Z')[0] : undefined,
	};
};

// Copied from Confluence advanced-search package
// generates truncated iso strings for Confluence last modified filter defaults (today, yesterday, etc)
// considering the user's locale-based definition of start OR end of day
export const getUTCDateRange = (fromOffset: number, toOffset: number) => {
	const currentDate = new Date();
	return {
		gt: new Date(
			new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime() -
				fromOffset * 24 * 60 * 60 * 1000,
		)
			.toISOString()
			.split('Z')[0],
		lt: new Date(
			new Date(
				currentDate.getFullYear(),
				currentDate.getMonth(),
				currentDate.getDate(),
				23,
				59,
				59,
				999,
			).getTime() -
				toOffset * 24 * 60 * 60 * 1000,
		)
			.toISOString()
			.split('Z')[0],
	};
};

export const convertLastUpdatedToDateRange = (lastUpdated: string) => {
	let dateRangeValues;
	switch (lastUpdated) {
		case 'today':
			dateRangeValues = getUTCDateRange(0, 0);
			break;
		case 'yesterday':
			dateRangeValues = getUTCDateRange(1, 1);
			break;
		case 'past7Days':
			dateRangeValues = getUTCDateRange(7, 0);
			break;
		case 'past30Days':
			dateRangeValues = getUTCDateRange(30, 0);
			break;
		case 'pastYear':
			dateRangeValues = getUTCDateRange(365, 0);
			break;
		default:
			dateRangeValues = { gt: '', lt: '' };
	}
	return dateRangeValues;
};

export const getConfluenceDateOptionFromCustomDates = (
	from: string,
	to: string,
): ConfluenceRangeFilter => {
	const { gt, lt } = convertCustomDateToRange(from, to);
	return {
		field: 'LASTMODIFIED',
		gt,
		lt,
	};
};

export const getConfluenceDateOptionValues = (
	selectedDateOption: string,
): ConfluenceRangeFilter => {
	const dateRangeValues = convertLastUpdatedToDateRange(selectedDateOption);
	const { gt, lt } = dateRangeValues;
	return {
		field: 'LASTMODIFIED',
		gt,
		lt,
	};
};

export type IntegrationMap = Partial<Record<ProductKeys, string>>;

export const baseIntegrationARIs: IntegrationMap = {
	[ProductKey.GoogleDrive]: GOOGLE_INTEGRATION_ARI,
	[ProductKey.Sharepoint]: MICROSOFT_INTEGRATION_ARI,
	[ProductKey.AtlassianWebCrawler]: WEB_CRAWLER_INTEGRATION_ARI,
	[ProductKey.Teams]: TEAMS_INTEGRATION_ARI,
	[ProductKey.Figma]: FIGMA_INTEGRATION_ARI,
	[ProductKey.Loom]: LOOM_INTEGRATION_ARI,
	[ProductKey.OneDrive]: MICROSOFT_INTEGRATION_ARI,
	[ProductKey.Github]: GITHUB_INTEGRATION_ARI,
	[ProductKey.Airtable]: AIRTABLE_INTEGRATION_ARI,
	[ProductKey.Notion]: NOTION_INTEGRATION_ARI,
	[ProductKey.Miro]: MIRO_INTEGRATION_ARI,
};

export const getThirdPartyFilters = (
	products: ProductKeys[],
	titleMatchValue: boolean,
	entities: TypeFilterValueKey[],
	outboundAuthConfigs: OutboundAuthConfigsType,
	baseIntegrationARIs: IntegrationMap,
	range?: ConfluenceRangeFilter[],
): ThirdPartyFilters => {
	if (
		!products.length ||
		products.some((p) => productConfigs[p]?.is3P || p === ProductKey.Bitbucket)
	) {
		const typeMapping = getTypeMapping();
		// Conditionally create the dynamic integration ARIs based on the config
		const getIntegrationARIsFromConfig = (): IntegrationMap => {
			const integrationMap: IntegrationMap = {};

			ThirdPartyConfigs.forEach((config, productKey3P) => {
				if (config.isEnabled()) {
					integrationMap[productKey3P] = config.integrationAri;
				}
			});

			return integrationMap;
		};

		const integrationARIsFromConfig = getIntegrationARIsFromConfig();

		// Combine static and dynamic integration ARIs
		// fg('rovo-full-page-search-3p-static-config') => false
		let productToIntegrationARIMap: IntegrationMap = false
			? integrationARIsFromConfig
			: baseIntegrationARIs;

		// this is not covered under 3p static config
		// fg('enable_rovo_search_bitbucket') => false
		if (false) {
			productToIntegrationARIMap = {
				...productToIntegrationARIMap,
				[ProductKey.Bitbucket]: BITBUCKET_INTEGRATION_ARI,
			};
		}

		// fg('rovo-full-page-search-3p-static-config') => false
		if (!false) {
			productToIntegrationARIMap = {
				...productToIntegrationARIMap,
				[ProductKey.Dropbox]: DROPBOX_INTEGRATION_ARI,
			};
			// fg('enable_3p_search_google_calendar') => false
			if (false) {
				productToIntegrationARIMap = {
					...productToIntegrationARIMap,
					[ProductKey.GoogleCalendar]: GOOGLE_INTEGRATION_ARI,
				};
			}
			// fg('enable_3p_search_gmail') => false
			if (false) {
				productToIntegrationARIMap = {
					...productToIntegrationARIMap,
					[ProductKey.Gmail]: GOOGLE_INTEGRATION_ARI,
				};
			}
			const isAllSelectedAndSlackAllDisabled = products.length > 1 && true;
			if (!isAllSelectedAndSlackAllDisabled) {
				productToIntegrationARIMap = {
					...productToIntegrationARIMap,
					[ProductKey.Slack]: SLACK_INTEGRATION_ARI,
				};
			}
		}
		// get the subtypes by looking at each type's subcategories
		const subtypes = entities.flatMap((entity) => typeMapping[entity]?.subCategories ?? []);
		if (Object.keys(productToIntegrationARIMap).length !== 0) {
			const integrationARIs: string[] = products
				.filter(
					(product) =>
						!productConfigs[product]?.is3P || !outboundAuthConfigs[product]?.userNeedsOAuth,
				)
				.map((product) => productToIntegrationARIMap[product])
				.filter((ari): ari is string => !!ari)
				//remove duplicates (eg sharepoint, teams)
				.filter((ari, pos, arr) => {
					return arr.indexOf(ari) === pos;
				});

			return {
				range,
				titleMatchOnly: titleMatchValue,
				subtypes,
				integrations: integrationARIs,
			};
		} else {
			return {
				range,
				titleMatchOnly: titleMatchValue,
				subtypes,
			};
		}
	}

	return {
		titleMatchOnly: false,
		subtypes: [],
		integrations: [],
	};
};

export enum DateRangeError {
	FROM_AFTER_TO_ERROR = 'fromAfterToError',
	FROM_IN_FUTURE_ERROR = 'fromInFutureError',
	TO_IN_FUTURE_ERROR = 'toInFutureError',
}

export const getInvalidDateRange = (from: string, to: string): DateRangeError | null => {
	if (!from && !to) {
		return null;
	}
	const dateFrom: Date = new Date(`${from}${from ? 'T00:00:00' : ''}`);
	const dateTo: Date = new Date(`${to}${to ? 'T00:00:00' : ''}`);

	if (dateFrom > dateTo) {
		return DateRangeError.FROM_AFTER_TO_ERROR;
	}

	return null;
};

export const getProducts3PFromConfigForFilter = (filterId: FilterId): ProductKeys3P[] => {
	const productsForFilter: ProductKeys3P[] = [];
	ThirdPartyConfigs.forEach((_, productKey3P) => {
		if (ThirdPartyConfigs.get(productKey3P)?.filters?.includes(filterId)) {
			productsForFilter.push(productKey3P);
		}
	});
	return productsForFilter;
};
